import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

export interface EraTextItem {
  x: number;
  y: number;
  text: string;
  size: number;
  page: number;
}

interface Row {
  page: number;
  y: number;
  items: EraTextItem[];
}

interface Adjustment {
  amount: string;
  carc: string;
  remark_ref: string | null;
}

interface ServiceLine {
  service_date?: string;
  cpt_code?: string;
  place_of_service?: string;
  amount_billed?: string;
  allowed_amount?: string;
  adjustments: Record<string, Adjustment[]>;
  total_paid?: string;
}

interface Remark {
  ref: string;
  group_code?: string;
  carc?: string;
  description: string;
}

interface Claim {
  client?: string;
  payer_claim_number?: string;
  clearinghouse_reference_number?: string;
  npi?: string;
  tax_id?: string;
  total_charges?: string;
  payment_amount?: string;
  copay_patient_resp?: string;
  responsibility_sequence?: string;
  service_lines: ServiceLine[];
  remarks: Remark[];
}

export interface EraPaymentReport {
  payment: {
    payer_name?: string;
    payer_address: string[];
    payee_name?: string;
    payee_address: string[];
    amount_paid?: string;
    payment_date?: string;
    check_eft_number?: string;
    payment_type?: string;
    payers_claim_office?: string;
  };
  claims: Claim[];
}

const DATE_RE = /^(\d{2})-(\d{2})-(\d{2})$/;
const ADJUSTMENT_RE = /\$([0-9,]+\.\d{2})\s*\((\d+)\)/g;
const REMARK_RE = /^(CO|CR|OA|PI|PR)\s*\((\d+)\)\s*,?\s*(.*)$/;
const FOOTER_RE = /^Page\s+\d+\s+of\s+\d+$/;
const ADJUSTMENT_COLUMNS = ['CO', 'CR', 'OA', 'PI', 'PR'] as const;
const SERVICE_COLUMNS = [
  ['service_date', 53],
  ['cpt_code', 95],
  ['place_of_service', 127],
  ['amount_billed', 152],
  ['allowed_amount', 194],
  ['CO', 236],
  ['CR', 294],
  ['OA', 352],
  ['PI', 410],
  ['PR', 468],
  ['total_paid', 526],
] as const;
const PAYMENT_LABELS: Record<string, keyof EraPaymentReport['payment']> = {
  'Amount Paid': 'amount_paid',
  'Payment Date': 'payment_date',
  'Check/EFT #': 'check_eft_number',
  'Payment Type': 'payment_type',
  'Payers Claim Office': 'payers_claim_office',
};
const CLAIM_LABELS: Record<string, Exclude<keyof Claim, 'service_lines' | 'remarks'>> = {
  Client: 'client',
  'Payer Claim Number': 'payer_claim_number',
  'Clearinghouse Reference Number': 'clearinghouse_reference_number',
  NPI: 'npi',
  'Tax ID': 'tax_id',
  'Total Charges': 'total_charges',
  'Payment Amount': 'payment_amount',
  'Copay / Patient Resp': 'copay_patient_resp',
  'Responsibility Sequence': 'responsibility_sequence',
};
const MONEY_FIELDS = new Set([
  'amount_paid',
  'total_charges',
  'payment_amount',
  'copay_patient_resp',
  'amount_billed',
  'allowed_amount',
  'total_paid',
]);

function isoDate(value: string): string {
  const match = DATE_RE.exec(value.trim());
  return match ? `20${match[3]}-${match[1]}-${match[2]}` : value.trim();
}

function normalizeMoney(value: string): string {
  return value.replaceAll('$', '').replaceAll(',', '').trim();
}

function normalizeValue(key: string, value: string): string {
  const text = value.trim();
  if (MONEY_FIELDS.has(key)) return normalizeMoney(text);
  if (key.endsWith('_date') || key === 'service_date') return isoDate(text);
  return text;
}

function clusterRows(items: EraTextItem[]): Row[] {
  const ordered = items
    .filter((item) => item.y >= 50 && !FOOTER_RE.test(item.text))
    .sort((a, b) => a.page - b.page || b.y - a.y || a.x - b.x);
  const rows: Row[] = [];
  const supers: EraTextItem[] = [];

  for (const item of ordered) {
    const previous = rows.at(-1);
    if (item.size < 6) {
      if (previous && previous.page === item.page && Math.abs(item.y - previous.y) < 8) {
        previous.items.push(item);
      } else {
        supers.push(item);
      }
      continue;
    }

    const row =
      previous && previous.page === item.page && Math.abs(item.y - previous.y) <= 2.2
        ? previous
        : { page: item.page, y: item.y, items: [] };
    if (row !== previous) rows.push(row);
    row.items.push(item);

    for (let index = supers.length - 1; index >= 0; index -= 1) {
      const superItem = supers[index];
      if (superItem.page === row.page && Math.abs(superItem.y - row.y) < 8) {
        row.items.push(superItem);
        supers.splice(index, 1);
      }
    }
  }
  return rows;
}

function rowTexts(row: Row): string[] {
  return [...row.items].sort((a, b) => a.x - b.x).map((item) => item.text);
}

function hasLabel(row: Row, label: string): boolean {
  return row.items.some((item) => item.text === label);
}

function labelValuePairs(row: Row): Record<string, string> {
  const items = [...row.items].sort((a, b) => a.x - b.x);
  const pairs: Record<string, string> = {};
  let index = 0;

  while (index < items.length) {
    const key = PAYMENT_LABELS[items[index].text] ?? CLAIM_LABELS[items[index].text];
    if (!key) {
      index += 1;
      continue;
    }
    const parts: string[] = [];
    let next = index + 1;
    while (next < items.length && !PAYMENT_LABELS[items[next].text] && !CLAIM_LABELS[items[next].text]) {
      parts.push(items[next].text);
      next += 1;
    }
    if (parts.length) pairs[key] = normalizeValue(key, parts.join(' '));
    index = next;
  }
  return pairs;
}

function serviceColumn(x: number): (typeof SERVICE_COLUMNS)[number][0] {
  let column: (typeof SERVICE_COLUMNS)[number][0] = 'service_date';
  for (const [name, start] of SERVICE_COLUMNS) {
    if (x + 4 >= start) column = name;
  }
  return column;
}

function parseServiceRow(row: Row): ServiceLine {
  const line: ServiceLine = { adjustments: {} };
  const columns = new Map<string, EraTextItem[]>();
  for (const item of row.items) {
    const column = serviceColumn(item.x);
    columns.set(column, [...(columns.get(column) ?? []), item]);
  }

  for (const [column, items] of columns) {
    const body = items.filter((item) => item.size >= 6).sort((a, b) => a.x - b.x);
    const remarkRef = items.find((item) => item.size < 6)?.text;
    const cell = body.map((item) => item.text).join(' ');
    if (ADJUSTMENT_COLUMNS.includes(column as (typeof ADJUSTMENT_COLUMNS)[number])) {
      const adjustments = [...cell.matchAll(ADJUSTMENT_RE)].map((match) => ({
        amount: normalizeMoney(match[1]),
        carc: match[2],
        remark_ref: remarkRef ?? null,
      }));
      if (adjustments.length) line.adjustments[column] = adjustments;
    } else if (cell) {
      (line as unknown as Record<string, unknown>)[column] = normalizeValue(column, cell);
    }
  }
  return line;
}

function appendRemark(claim: Claim, row: Row): void {
  let ref = '';
  const parts: string[] = [];
  for (const item of [...row.items].sort((a, b) => a.x - b.x || b.size - a.size)) {
    if (!ref && item.size < 6 && /^\d+$/.test(item.text)) ref = item.text;
    else parts.push(item.text);
  }
  const text = parts.join(' ').trim();
  if (!text) return;
  if (!ref && claim.remarks.length) {
    const previous = claim.remarks.at(-1);
    if (previous) previous.description = `${previous.description} ${text}`.trim();
    return;
  }
  const match = REMARK_RE.exec(text);
  claim.remarks.push({
    ref: ref || String(claim.remarks.length + 1),
    ...(match ? { group_code: match[1], carc: match[2] } : {}),
    description: (match?.[3] ?? text).trim(),
  });
}

function splitHeaderAddresses(report: EraPaymentReport, lines: string[]): void {
  if (!lines.length) return;
  let splitAt = 1;
  for (let index = 1; index < lines.length; index += 1) {
    if (/^[A-Z0-9][A-Z0-9 .,'&/-]{2,}$/.test(lines[index])) {
      splitAt = index;
      break;
    }
  }
  report.payment.payer_name = lines[0];
  report.payment.payer_address = lines.slice(1, splitAt);
  if (splitAt < lines.length) {
    report.payment.payee_name = lines[splitAt];
    report.payment.payee_address = lines.slice(splitAt + 1);
  }
}

export function parseEraTextItems(items: EraTextItem[]): EraPaymentReport {
  const report: EraPaymentReport = {
    payment: { payer_address: [], payee_address: [] },
    claims: [],
  };
  const headerAddress: string[] = [];
  let claim: Claim | undefined;
  let inRemarks = false;

  for (const row of clusterRows(items)) {
    const texts = rowTexts(row);
    if (
      hasLabel(row, 'Explanation of Benefits') ||
      (texts.includes('POS') && texts.includes('CO') && (texts.includes('CPT') || texts.includes('Code'))) ||
      texts.every((text) =>
        ['Service', 'dates', 'CPT', 'Code', 'Amount', 'Billed', 'Allowed', 'Adjustments', 'Total', 'Paid'].includes(text),
      )
    ) {
      continue;
    }

    const pairs = labelValuePairs(row);
    for (const key of Object.values(PAYMENT_LABELS)) {
      if (pairs[key]) (report.payment[key] as string | undefined) = pairs[key];
    }

    if (pairs.client) {
      claim = { service_lines: [], remarks: [] };
      report.claims.push(claim);
      inRemarks = false;
    }
    if (Object.values(CLAIM_LABELS).some((key) => pairs[key])) {
      claim ??= { service_lines: [], remarks: [] };
      if (!report.claims.includes(claim)) report.claims.push(claim);
      for (const key of Object.values(CLAIM_LABELS)) {
        if (pairs[key]) (claim[key] as string | undefined) = pairs[key];
      }
      continue;
    }

    if (hasLabel(row, 'Remarks')) {
      inRemarks = true;
      continue;
    }

    const body = row.items.filter((item) => item.size >= 6);
    const leftmost = body.length ? body.reduce((left, item) => (item.x < left.x ? item : left)) : undefined;
    if (claim && leftmost && leftmost.x < 80 && DATE_RE.test(leftmost.text)) {
      inRemarks = false;
      claim.service_lines.push(parseServiceRow(row));
      continue;
    }
    if (claim && inRemarks) {
      appendRemark(claim, row);
      continue;
    }
    if (!report.claims.length) {
      const left = row.items.filter((item) => item.x < 300 && item.size >= 6);
      if (left.length && !left.some((item) => PAYMENT_LABELS[item.text])) {
        headerAddress.push(left.sort((a, b) => a.x - b.x).map((item) => item.text).join(' '));
      }
    }
  }

  splitHeaderAddresses(report, headerAddress);
  return report;
}

export async function parseEraPdf(file: File): Promise<EraPaymentReport> {
  const { GlobalWorkerOptions, getDocument } = await import('pdfjs-dist');
  GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  const loadingTask = getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  try {
    const pdf = await loadingTask.promise;
    const items: EraTextItem[] = [];

    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      for (const item of content.items) {
        if (!('str' in item)) continue;
        const text = item.str.trim();
        if (!text) continue;
        items.push({
          x: item.transform[4],
          y: item.transform[5],
          text,
          size: item.height,
          page: pageNumber,
        });
      }
    }
    return parseEraTextItems(items);
  } finally {
    await loadingTask.destroy();
  }
}
